import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { createProgram, listPrograms, listTemplates, type Program, type ProgramCreate } from "@/api/client";
import { useSession } from "@/auth/session";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { useEmailTemplateChoices } from "@/features/email/choices";
import { ProgramForm } from "@/features/programs/ProgramForm";
import { EMPTY_PROGRAM_FORM, formatMembershipNo, toCreateBody } from "@/features/programs/programValues";
import { ScrollArea } from "@/components/ScrollArea";

/** Templates for naming and choosing, or null when the user can't read them. */
export function useTemplateChoices() {
  const { can } = useSession();
  const allowed = can("template.read");
  const templates = useQuery({ queryKey: ["templates"], queryFn: listTemplates, enabled: allowed });
  return { templates: allowed ? (templates.data ?? []) : null, error: templates.error, isLoading: allowed && templates.isLoading };
}

export function numberScheme(program: Program) {
  return `${program.membership_no_prefix || "No prefix"} + ${program.membership_no_width ?? 5} digits`;
}

export function nextNumber(program: Program) {
  return formatMembershipNo(program.membership_no_prefix ?? "", program.membership_no_width ?? 5, program.membership_no_next ?? 1);
}

export function ProgramList() {
  const { can } = useSession();
  const programs = useQuery({ queryKey: ["programs"], queryFn: listPrograms });
  const { templates } = useTemplateChoices();
  const names = new Map((templates ?? []).map((t) => [t.id, t.name]));
  const items = programs.data ?? [];
  const [creating, setCreating] = useState(false);

  const templateLabel = (program: Program) => {
    if (!program.template_id) return "None";
    if (templates === null) return program.template_id;
    return names.get(program.template_id) ?? program.template_id;
  };

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title="Programs"
        detail="Induction programs, their membership numbers and templates"
        action={
          can("program.manage") ? (
            <ToolbarButton disabled={creating} onClick={() => setCreating(true)}>
              New program
            </ToolbarButton>
          ) : null
        }
      />
      <ErrorBanner error={programs.error} />
      {creating ? <NewProgram onCancel={() => setCreating(false)} /> : null}

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        {programs.isLoading ? (
          <EmptyState title="Loading programs" detail="Fetching programs." />
        ) : programs.isSuccess && items.length === 0 ? (
          <EmptyState title="No programs yet" detail="Create one for each induction you issue certificates for." />
        ) : (
          <ScrollArea label="Programs">
            <table aria-label="Programs" className="w-full min-w-[860px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Induction date</th>
                  <th className="px-4 py-3 font-medium">Numbering</th>
                  <th className="px-4 py-3 font-medium">Next number</th>
                  <th className="px-4 py-3 font-medium">Payment</th>
                  <th className="px-4 py-3 font-medium">Template</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((program) => (
                  <tr key={program.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <Link
                        to="/programs/$programId"
                        params={{ programId: program.id }}
                        className="font-medium text-zinc-950 underline-offset-2 hover:underline"
                      >
                        {program.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{program.program_type}</td>
                    <td className="px-4 py-3 text-zinc-700">{program.induction_date ?? "No date"}</td>
                    <td className="px-4 py-3 text-zinc-700">{numberScheme(program)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-700">{nextNumber(program)}</td>
                    <td className="px-4 py-3 text-zinc-700">{program.requires_payment ? "Required" : "Not required"}</td>
                    <td className="px-4 py-3 text-zinc-700">{templateLabel(program)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>
    </section>
  );
}

function NewProgram({ onCancel }: { onCancel: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { templates, error: templatesError, isLoading: templatesLoading } = useTemplateChoices();
  const emailChoices = useEmailTemplateChoices();
  const create = useMutation({
    mutationFn: (body: ProgramCreate) => createProgram(body),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["programs"] });
      queryClient.setQueryData(["program", created.id], created);
      await navigate({ to: "/programs/$programId", params: { programId: created.id } });
    },
  });
  return (
    <section aria-labelledby="new-program" className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 id="new-program" className="text-sm font-semibold">
        New program
      </h2>
      <ProgramForm
        initial={EMPTY_PROGRAM_FORM}
        templates={templates}
        emailTemplates={emailChoices.templates}
        numberLabel="First number"
        notes={() =>
          templates === null ? (
            <p className="text-xs text-zinc-500">Choosing a template needs template.read, so this program starts without one.</p>
          ) : templatesError ? (
            <p role="alert" className="text-sm text-red-700">
              Templates could not be loaded: {templatesError.message}. Reload before creating a program that needs one.
            </p>
          ) : templatesLoading ? (
            <p className="text-xs text-zinc-500">Loading templates...</p>
          ) : null
        }
        submitLabel="Create program"
        pendingLabel="Creating..."
        pending={create.isPending}
        error={create.error}
        autoFocus
        onEdit={() => {
          if (create.error) create.reset();
        }}
        onSubmit={(values) => create.mutate(toCreateBody(values))}
        onCancel={onCancel}
      />
    </section>
  );
}
